import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BOOK_REVIEW_MAILTO } from "@/lib/brand";
import HeroAccent from "@/components/site/HeroAccent";

const LETTERS = ["R", "e", "i", "m", "a", "g", "i", "n", "e"];

const TICKER = [
  "Reimagine structure",
  "Reimagine communication",
  "Reimagine workflow",
  "Reimagine admin",
  "Reimagine the day-to-day",
];

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-24 md:pt-28 pb-14 md:pb-20 overflow-hidden bg-bg-base"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        {/* Top eyebrow with pulsing dot */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="flex items-center gap-3"
        >
          <span className="relative inline-flex items-center justify-center">
            <span className="absolute inline-block w-2 h-2 rounded-full bg-brand-red/40 pulse-soft" />
            <span className="relative inline-block w-2 h-2 rounded-full bg-brand-red" />
          </span>
          <span className="overline text-ink-soft">
            Business · Marketing · Technology · AI
          </span>
        </motion.div>

        <div className="mt-6 md:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Headline column */}
          <div className="lg:col-span-8">
            <h1
              data-testid="hero-headline"
              className="font-serif-display leading-[0.86] tracking-tightest text-ink"
            >
              <span className="block text-[19vw] sm:text-[17vw] md:text-[13vw] lg:text-[11.5vw] font-medium">
                <span className="sr-only">Reimagine</span>
                <span aria-hidden className="inline-flex items-end">
                  {LETTERS.map((ch, i) => (
                    <span key={i} className="letter-mask">
                      <motion.span
                        initial={{ y: "110%" }}
                        animate={{ y: "0%" }}
                        transition={{
                          duration: 1.05,
                          delay: 0.25 + i * 0.085,
                          ease: [0.22, 1, 0.36, 1],
                        }}
                        className="inline-block"
                      >
                        {ch}
                      </motion.span>
                    </span>
                  ))}
                  <motion.span
                    initial={{ y: "-40%", opacity: 0, scale: 0.6 }}
                    animate={{ y: "0%", opacity: 1, scale: 1 }}
                    transition={{ delay: 1.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="inline-block"
                  >
                    <span className="brand-dot" />
                  </motion.span>
                </span>
              </span>

              <motion.span
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
                className="block italic font-light text-ink-soft text-2xl sm:text-3xl md:text-5xl lg:text-6xl mt-3 md:mt-4 tracking-tight"
              >
                {"how "}
                <span className="brush-underline">
                  business
                  <motion.span
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1.1, delay: 1.7, ease: [0.22, 1, 0.36, 1] }}
                    className="brush-stroke origin-left"
                  />
                </span>
                {" works."}
              </motion.span>
            </h1>
          </div>

          {/* Accent column */}
          <div className="lg:col-span-4 hidden lg:block">
            <HeroAccent />
          </div>
        </div>

        <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            data-testid="hero-supporting"
            className="lg:col-span-7 text-lg md:text-2xl leading-snug font-light text-ink/85 max-w-2xl"
          >
            Business, marketing, technology and AI support for practical
            businesses ready to grow with structure. From trade and trust, to
            paper and process, to websites, workflows and AI — we help
            businesses bring the pieces together.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="lg:col-span-5 flex flex-col sm:flex-row lg:justify-end gap-3 sm:gap-4"
          >
            <a
              href={BOOK_REVIEW_MAILTO}
              data-testid="hero-cta-primary"
              className="group relative inline-flex items-center justify-center gap-3 bg-brand-red text-white px-7 py-4 text-[13.5px] tracking-[0.06em] font-medium overflow-hidden shadow-[0_4px_18px_-6px_rgba(230,51,41,0.5)]"
            >
              <span className="absolute inset-0 bg-brand-redHover translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)]" />
              <span className="relative">Book a Review</span>
              <span aria-hidden className="relative transition-transform duration-500 group-hover:translate-x-1">→</span>
            </a>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="group relative inline-flex items-center justify-center gap-2 border border-ink/80 text-ink px-7 py-4 text-[13.5px] tracking-[0.06em] font-medium overflow-hidden bg-white/60"
            >
              <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)]" />
              <span className="relative group-hover:text-bg-base transition-colors duration-500">Explore Services</span>
            </Link>
          </motion.div>
        </div>

        {/* Animated ticker */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-14 md:mt-16 -mx-6 md:-mx-10 border-y border-ink/10 py-3 overflow-hidden no-scrollbar bg-white"
          aria-hidden
        >
          <div className="ticker-track flex gap-10 md:gap-14 whitespace-nowrap w-max">
            {[...TICKER, ...TICKER, ...TICKER].map((t, i) => (
              <div key={i} className="flex items-center gap-10 md:gap-14">
                <span className="font-serif-display italic text-xl md:text-2xl text-ink">
                  {t}
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-brand-brass shrink-0" />
              </div>
            ))}
          </div>
        </motion.div>

        {/* Four pillars at-a-glance */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-10 md:mt-12"
        >
          <div className="mb-5">
            <span className="overline text-ink-soft">
              What we help you reimagine
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
            {[
              ["01", "Business Structure", "bg-brand-red"],
              ["02", "Marketing Communications", "bg-brand-brass"],
              ["03", "Technology Systems", "bg-brand-teal"],
              ["04", "Practical AI Adoption", "bg-ink"],
            ].map(([n, label, dot], i) => (
              <motion.div
                key={n}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.55 + i * 0.07, duration: 0.6 }}
                className="flex items-start gap-3"
              >
                <span className="overline text-ink-soft mt-0.5">{n}</span>
                <span className="flex items-center gap-2 text-ink text-[15px] leading-snug font-medium">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
                  {label}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
